class MedievalCollectionFilters {
  constructor() {
    this.requestController = null;
    this.priceTimer = null;
    document.addEventListener('submit', this.handleSubmit);
    document.addEventListener('change', this.handleChange);
    document.addEventListener('input', this.handleInput);
    document.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('resize', this.handleImageLoadingResize);
    this.updateCollectionImageLoading(document);
    this.loadSearchFallbacks();
  }

  loadSearchFallbacks() {
    document.querySelectorAll('[data-collection-search-fallback]').forEach((placeholder) => {
      this.loadSearchFallback(placeholder);
    });
  }

  async loadSearchFallback(placeholder) {
    const searchUrl = new URL(placeholder.dataset.searchUrl, window.location.origin);
    const publicUrl = new URL(window.location.href);
    for (const [name, value] of publicUrl.searchParams.entries()) {
      if (name === 'q' || name === 'type' || name === 'view' || name === 'section_id') continue;
      searchUrl.searchParams.append(name, value);
    }

    try {
      const response = await fetch(searchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Large collection fallback failed with ${response.status}`);
      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextRoot = responseDocument.querySelector('[data-search-collection-fallback]');
      if (!nextRoot) throw new Error('Large collection fallback was not returned');
      nextRoot.querySelectorAll('noscript').forEach((element) => element.remove());
      const collectionShell = placeholder.closest('.tm-collection');
      const nativeLayout = collectionShell?.querySelector('[data-native-collection-layout]');
      const hasActiveFilters = [...publicUrl.searchParams.keys()].some((name) => name.startsWith('filter.'));
      if (nativeLayout && !hasActiveFilters) {
        const nativeResults = nativeLayout.querySelector('.tm-collection__results');
        const fallbackResults = nextRoot.querySelector('.tm-collection__results');
        const nativeGrid = nativeResults?.querySelector('.tm-collection__grid');
        const fallbackGrid = fallbackResults?.querySelector('.tm-collection__grid');
        if (nativeGrid && fallbackGrid) fallbackGrid.replaceWith(nativeGrid);

        const nativePagination = nativeResults?.querySelector('.tm-pagination');
        const fallbackPagination = fallbackResults?.querySelector('.tm-pagination');
        if (nativePagination) {
          nativePagination.querySelectorAll('a').forEach((link) => link.setAttribute('data-native-pagination', ''));
          if (fallbackPagination) fallbackPagination.replaceWith(nativePagination);
          else fallbackResults?.append(nativePagination);
        }

        const nativeControls = nativeResults?.querySelector('.tm-collection__controls');
        const fallbackControls = fallbackResults?.querySelector('.tm-collection__controls');
        const fallbackFilterWrap = fallbackControls?.querySelector('.tm-collection__filter-wrap');
        const nativeFilterWrap = nativeControls?.querySelector('.tm-collection__filter-wrap');
        if (nativeControls && fallbackControls) {
          if (fallbackFilterWrap && nativeFilterWrap) nativeFilterWrap.replaceWith(fallbackFilterWrap);
          nativeControls.classList.add('tm-collection__controls--sidebar');
          fallbackControls.replaceWith(nativeControls);
        }

        nativeResults?.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
          fallbackResults?.append(script);
        });
        nativeLayout.remove();
        nextRoot.setAttribute('data-native-unfiltered', '');
      }
      // Filtered search results must not be replaced with the unfiltered collection.
      nativeLayout?.remove();
      nextRoot.dataset.filterPublicPath = placeholder.dataset.publicPath;
      nextRoot.dataset.filterRequestUrl = placeholder.dataset.searchUrl;
      this.preserveCollectionProductLinks(nextRoot);
      this.updateCollectionImageSizes(nextRoot, collectionShell);
      placeholder.replaceWith(nextRoot);
      this.updateCollectionImageLoading(nextRoot);
    } catch (error) {
      placeholder.classList.add('tm-collection__fallback-error');
      placeholder.querySelector('.tm-collection__fallback-spinner')?.remove();
      const message = placeholder.dataset.errorMessage;
      if (message) placeholder.querySelector('p').textContent = message;
    }
  }

  updateCollectionImageLoading(root) {
    root.querySelectorAll('.tm-collection__grid').forEach((grid) => {
      const firstImage = grid.querySelector('.tm-product-card')?.querySelector('.tm-product-card__image');
      grid.querySelectorAll('.tm-product-card__image').forEach((image) => {
        const bounds = image.getBoundingClientRect();
        const visible = bounds.width > 0 && bounds.height > 0
          && bounds.top < window.innerHeight && bounds.bottom > 0
          && bounds.left < window.innerWidth && bounds.right > 0;
        const loading = image === firstImage || visible ? 'eager' : 'lazy';
        const sizes = image.sizes.replace(/^auto\s*,\s*/, '');
        image.sizes = loading === 'lazy' ? `auto, ${sizes || '100vw'}` : sizes || '100vw';
        image.loading = loading;
      });
    });
  }

  handleImageLoadingResize = () => {
    if (this.imageLoadingFrame) return;
    this.imageLoadingFrame = window.requestAnimationFrame(() => {
      this.imageLoadingFrame = null;
      this.updateCollectionImageLoading(document);
    });
  };

  updateCollectionImageSizes(root, collectionShell) {
    const layout = root.querySelector('.tm-collection__layout');
    if (!layout || !collectionShell) return;
    const sizes = layout.classList.contains('tm-collection__layout--full')
      ? collectionShell.dataset.productImageSizesFull
      : collectionShell.dataset.productImageSizesSidebar;
    if (!sizes) return;
    root.querySelectorAll('.tm-product-card__image').forEach((image) => {
      image.sizes = image.loading === 'lazy' ? `auto, ${sizes}` : sizes;
    });
  }

  preserveCollectionProductLinks(root) {
    if (!root.dataset.filterPublicPath) return;
    const collectionUrl = new URL(root.dataset.filterPublicPath, window.location.origin);
    if (collectionUrl.origin !== window.location.origin || !/\/collections\/[^/]+\/?$/.test(collectionUrl.pathname)) return;
    root.querySelectorAll('.tm-product-card__image-link, .tm-product-card__title a').forEach((link) => {
      const productUrl = new URL(link.href, window.location.origin);
      const productPath = productUrl.pathname.match(/\/products\/[^/]+\/?$/)?.[0];
      if (productUrl.origin !== window.location.origin || !productPath) return;
      link.setAttribute('href', `${collectionUrl.pathname.replace(/\/$/, '')}${productPath}${productUrl.search}${productUrl.hash}`);
    });
  }

  handleSubmit = (event) => {
    const form = event.target.closest('[data-collection-filter-form]');
    if (!form) return;
    event.preventDefault();
    this.clearPriceTimer();
    this.updateFromForm(form, event.submitter || document.activeElement);
  };

  handleChange = (event) => {
    const form = event.target.closest('[data-collection-filter-form]');
    if (!form) return;
    const root = form.closest('[data-collection-section]');
    if (root?.hasAttribute('data-native-unfiltered') && event.target.matches('select[name="sort_by"]')) {
      const url = new URL(root.dataset.filterPublicPath || window.location.pathname, window.location.origin);
      url.searchParams.set('sort_by', event.target.value);
      window.location.assign(url);
      return;
    }
    if (event.target.matches('[data-price-range-input]')) {
      this.syncPriceRange(event.target);
    }
    this.clearPriceTimer();
    this.updateFromForm(form, event.target);
  };

  handleInput = (event) => {
    if (event.target.matches('[data-price-range-input]')) {
      this.syncPriceRange(event.target);
    } else if (!event.target.matches('[data-collection-filter-form] input[type="number"]')) {
      return;
    }
    const form = event.target.form;
    if (!form) return;
    this.clearPriceTimer();
    this.priceTimer = window.setTimeout(() => this.updateFromForm(form, event.target), 450);
  };

  handleClick = (event) => {
    const root = event.target.closest('[data-collection-section]');
    if (!root) return;
    const closeButton = event.target.closest('[data-mobile-filter-close]');
    if (closeButton) {
      const panel = closeButton.closest('[data-mobile-filter-panel]');
      if (panel) {
        panel.open = false;
        panel.querySelector(':scope > summary')?.focus({ preventScroll: true });
      }
      return;
    }
    const link = event.target.closest('[data-collection-filter-link], .tm-pagination a');
    if (!link) return;
    if (link.hasAttribute('data-native-pagination')) return;
    event.preventDefault();
    this.clearPriceTimer();
    this.update(link.href, { source: link, scrollToTop: Boolean(link.closest('.tm-pagination')) });
  };

  handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const panel = document.querySelector('[data-mobile-filter-panel][open]');
    if (!panel) return;
    panel.open = false;
    panel.querySelector(':scope > summary')?.focus({ preventScroll: true });
  };

  handlePopState = () => {
    const root = document.querySelector('[data-collection-section]');
    if (!root) return;
    this.update(window.location.href, { historyMode: 'none' });
  };

  clearPriceTimer() {
    if (!this.priceTimer) return;
    window.clearTimeout(this.priceTimer);
    this.priceTimer = null;
  }

  syncPriceRange(source) {
    const range = source.closest('[data-price-range]');
    if (!range) return;

    const minInput = range.querySelector('[data-price-range-input="min"]');
    const maxInput = range.querySelector('[data-price-range-input="max"]');
    const minField = range.querySelector('[data-price-filter-value="min"]');
    const maxField = range.querySelector('[data-price-filter-value="max"]');
    const minOutput = range.querySelector('[data-price-output="min"]');
    const maxOutput = range.querySelector('[data-price-output="max"]');
    const rangeMax = Number(range.dataset.rangeMax) || 0;
    if (!minInput || !maxInput || !minField || !maxField || rangeMax <= 0) return;

    let minValue = Number(minInput.value);
    let maxValue = Number(maxInput.value);
    if (source.dataset.priceRangeInput === 'min' && minValue > maxValue) {
      minValue = maxValue;
      minInput.value = String(minValue);
    } else if (source.dataset.priceRangeInput === 'max' && maxValue < minValue) {
      maxValue = minValue;
      maxInput.value = String(maxValue);
    }

    const priceTolerance = 0.005;
    const hasMinimum = minValue > priceTolerance;
    const hasMaximum = maxValue < rangeMax - priceTolerance;
    minField.value = hasMinimum ? String(minValue) : '';
    maxField.value = hasMaximum ? String(maxValue) : '';
    minField.disabled = !hasMinimum;
    maxField.disabled = !hasMaximum;
    range.style.setProperty('--price-range-start', `${(minValue / rangeMax) * 100}%`);
    range.style.setProperty('--price-range-end', `${(maxValue / rangeMax) * 100}%`);
    minInput.style.zIndex = minValue > rangeMax * 0.9 ? '5' : '3';

    const currency = range.dataset.currency;
    if (currency) {
      const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      });
      if (minOutput) minOutput.textContent = formatter.format(minValue);
      if (maxOutput) maxOutput.textContent = formatter.format(maxValue);
    }
  }

  updateFromForm(form, source) {
    form.querySelectorAll('[data-price-range]').forEach((range) => {
      const priceSource = range.querySelector('[data-price-range-input="min"]');
      if (priceSource) this.syncPriceRange(priceSource);
    });
    const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    const formData = new FormData(form);
    url.search = '';
    for (const [name, value] of formData.entries()) {
      if (String(value).trim() !== '') url.searchParams.append(name, value);
    }
    url.searchParams.delete('page');
    this.update(url, { source });
  }

  captureState(root, source) {
    const focusedElement = source instanceof HTMLInputElement || source instanceof HTMLSelectElement ? source : null;
    return {
      openGroups: [...root.querySelectorAll('[data-filter-group][open]')].map((group) => group.dataset.filterGroup),
      mobilePanelOpen: root.querySelector('[data-mobile-filter-panel]')?.open || false,
      sidebarScroll: root.querySelector('.tm-collection__sidebar-form')?.scrollTop || 0,
      focus: focusedElement ? {
        name: focusedElement.name,
        value: focusedElement.value,
        type: focusedElement.type,
        priceRangeInput: focusedElement.dataset.priceRangeInput || null,
        scope: focusedElement.closest('.tm-collection__sidebar-form') ? 'sidebar' : 'controls',
      } : null,
    };
  }

  restoreState(root, state) {
    root.querySelectorAll('[data-filter-group]').forEach((group) => {
      group.open = state.openGroups.includes(group.dataset.filterGroup);
    });

    const mobilePanel = root.querySelector('[data-mobile-filter-panel]');
    if (mobilePanel) mobilePanel.open = state.mobilePanelOpen;

    const sidebarForm = root.querySelector('.tm-collection__sidebar-form');
    if (sidebarForm) sidebarForm.scrollTop = state.sidebarScroll;

    if (!state.focus?.name) return;
    const focusScope = state.focus.scope === 'sidebar'
      ? '.tm-collection__sidebar-form'
      : '.tm-collection__controls';
    if (state.focus.priceRangeInput) {
      const priceRangeTarget = root.querySelector(
        `${focusScope} [data-price-range-input="${CSS.escape(state.focus.priceRangeInput)}"]`,
      );
      window.requestAnimationFrame(() => priceRangeTarget?.focus({ preventScroll: true }));
      return;
    }
    const candidates = [...root.querySelectorAll(`${focusScope} [name="${CSS.escape(state.focus.name)}"]`)];
    const focusTarget = candidates.find((candidate) => candidate.value === state.focus.value) || candidates[0];
    window.requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
  }

  async update(destination, { historyMode = 'push', source = null, scrollToTop = false } = {}) {
    const currentRoot = source?.closest('[data-collection-section]') || document.querySelector('[data-collection-section]');
    if (!currentRoot) return;

    const destinationUrl = new URL(destination, window.location.href);
    const isSearchFallback = currentRoot.hasAttribute('data-filter-fallback');
    const destinationHasFilters = [...destinationUrl.searchParams.keys()].some((name) => name.startsWith('filter.'));
    let publicUrl = new URL(destinationUrl);
    let requestUrl = new URL(destinationUrl);

    if (isSearchFallback) {
      publicUrl = new URL(currentRoot.dataset.filterPublicPath || window.location.pathname, window.location.origin);
      requestUrl = new URL(currentRoot.dataset.filterRequestUrl, window.location.origin);
      for (const [name, value] of destinationUrl.searchParams.entries()) {
        if (name === 'q' || name === 'type' || name === 'view' || name === 'section_id') continue;
        publicUrl.searchParams.append(name, value);
        if (name === 'sort_by') requestUrl.searchParams.delete('sort_by');
        requestUrl.searchParams.append(name, value);
      }
      const searchSortOptions = ['relevance', 'price-ascending', 'price-descending'];
      if (destinationHasFilters && !searchSortOptions.includes(publicUrl.searchParams.get('sort_by'))) {
        publicUrl.searchParams.set('sort_by', 'relevance');
        requestUrl.searchParams.set('sort_by', 'relevance');
      }
    }

    publicUrl.searchParams.delete('section_id');
    if (isSearchFallback && !destinationHasFilters && !currentRoot.hasAttribute('data-native-unfiltered')) {
      window.location.assign(publicUrl);
      return;
    }
    if (historyMode === 'push' && publicUrl.toString() === window.location.href) return;

    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    const state = this.captureState(currentRoot, source);
    requestUrl.searchParams.set('section_id', currentRoot.dataset.sectionId);
    currentRoot.setAttribute('data-loading', '');
    currentRoot.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Collection filtering failed with ${response.status}`);
      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextRoot = responseDocument.querySelector('[data-collection-section]');
      if (!nextRoot) throw new Error('Collection section was not returned');
      nextRoot.querySelectorAll('noscript').forEach((element) => element.remove());

      if (isSearchFallback) {
        nextRoot.dataset.filterPublicPath = currentRoot.dataset.filterPublicPath;
        nextRoot.dataset.filterRequestUrl = currentRoot.dataset.filterRequestUrl;
        this.preserveCollectionProductLinks(nextRoot);
        this.updateCollectionImageSizes(nextRoot, currentRoot.closest('.tm-collection'));
      }

      currentRoot.replaceWith(nextRoot);
      this.updateCollectionImageLoading(nextRoot);
      this.restoreState(nextRoot, state);
      if (historyMode === 'push') {
        window.history.pushState({ collectionFilters: true }, '', publicUrl);
      }
      if (scrollToTop) window.scrollTo(0, 0);
      document.dispatchEvent(new CustomEvent('theme:collection:updated', {
        detail: { sectionId: nextRoot.dataset.sectionId, url: publicUrl.toString() },
      }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(isSearchFallback ? requestUrl : publicUrl);
    } finally {
      if (this.requestController === controller) this.requestController = null;
      if (currentRoot.isConnected) {
        currentRoot.removeAttribute('data-loading');
        currentRoot.removeAttribute('aria-busy');
      }
    }
  }
}

if (!window.medievalCollectionFilters) {
  window.medievalCollectionFilters = new MedievalCollectionFilters();
}
